/**
 * First-order radioactive decay: N = N₀ · (½)^(t / T½).
 *
 * Every quantity is held in SI seconds internally; `TIME_UNITS` converts to and from the
 * units a reader types. The year is the Julian year of exactly 365.25 days (31,557,600 s),
 * the convention of the IAU and of radioactive-decay tables; the day is 86,400 s.
 *
 * Related quantities: the decay constant λ = ln 2 / T½ and the mean lifetime
 * τ = 1 / λ = T½ / ln 2 ≈ 1.4427 T½. Amounts N₀ and N may be in any consistent unit
 * (grams, becquerels, atoms): only their ratio matters.
 */

export interface TimeUnit {
  id: string;
  symbol: string;
  /** Seconds in one of these. */
  seconds: number;
}

const DAY = 86_400;
const JULIAN_YEAR = 365.25 * DAY;

export const TIME_UNITS: readonly TimeUnit[] = [
  { id: "us", symbol: "µs", seconds: 1e-6 },
  { id: "ms", symbol: "ms", seconds: 1e-3 },
  { id: "s", symbol: "s", seconds: 1 },
  { id: "min", symbol: "min", seconds: 60 },
  { id: "h", symbol: "h", seconds: 3600 },
  { id: "d", symbol: "d", seconds: DAY },
  { id: "y", symbol: "y", seconds: JULIAN_YEAR },
  { id: "ky", symbol: "ky", seconds: 1e3 * JULIAN_YEAR },
  { id: "My", symbol: "My", seconds: 1e6 * JULIAN_YEAR },
  { id: "Gy", symbol: "Gy", seconds: 1e9 * JULIAN_YEAR },
];

export function findTimeUnit(id: string): TimeUnit {
  return TIME_UNITS.find((unit) => unit.id === id) ?? TIME_UNITS[2];
}

export function timeToSeconds(value: number, unitId: string): number {
  return value * findTimeUnit(unitId).seconds;
}

export function secondsToUnit(seconds: number, unitId: string): number {
  return seconds / findTimeUnit(unitId).seconds;
}

export type HalfLifeSolve = "remaining" | "initial" | "time" | "halfLife";

export type HalfLifeError =
  | "amount" // an amount is zero or negative
  | "remainingTooBig" // N > N₀
  | "time" // t negative
  | "halfLife" // T½ zero or negative
  | "noDecay"; // N = N₀ with t > 0, or t = 0 with N < N₀ : no half-life fits

export interface HalfLifeState {
  /** N₀ */
  initial: number;
  /** N */
  remaining: number;
  /** t, seconds */
  timeSec: number;
  /** T½, seconds */
  halfLifeSec: number;
}

export interface HalfLifeResult extends HalfLifeState {
  /** t / T½ — the number of half-lives elapsed. */
  halfLives: number;
  /** N / N₀ */
  fraction: number;
  /** λ in 1/s */
  decayConstant: number;
  /** τ in s */
  meanLifeSec: number;
}

export interface Known {
  initial: number | null;
  remaining: number | null;
  timeSec: number | null;
  halfLifeSec: number | null;
}

const isPositive = (v: number | null): v is number =>
  v !== null && Number.isFinite(v) && v > 0;
const isNonNegative = (v: number | null): v is number =>
  v !== null && Number.isFinite(v) && v >= 0;

/** Fraction left after k half-lives, ½^k. */
export function fractionAfter(halfLives: number): number {
  return 0.5 ** halfLives;
}

function finish(state: HalfLifeState): HalfLifeResult {
  const decayConstant = Math.LN2 / state.halfLifeSec;
  return {
    ...state,
    halfLives: state.timeSec / state.halfLifeSec,
    fraction: state.remaining / state.initial,
    decayConstant,
    meanLifeSec: 1 / decayConstant,
  };
}

/** Validation shared by the four solves, on whatever is known. */
function checkKnown(known: Known): HalfLifeError | null {
  const { initial, remaining, timeSec, halfLifeSec } = known;
  if (
    (initial !== null && !isPositive(initial)) ||
    (remaining !== null && !isPositive(remaining))
  ) {
    return "amount";
  }
  if (initial !== null && remaining !== null && remaining > initial) {
    return "remainingTooBig";
  }
  if (timeSec !== null && !isNonNegative(timeSec)) {
    return "time";
  }
  if (halfLifeSec !== null && !isPositive(halfLifeSec)) {
    return "halfLife";
  }
  return null;
}

/**
 * Solve for the unknown named by `solve`. Returns null while the three other values are
 * not all entered, and an error code when they cannot describe a decay.
 */
export function solveHalfLife(
  solve: HalfLifeSolve,
  known: Known
): HalfLifeResult | { error: HalfLifeError } | null {
  const needed = {
    remaining: ["initial", "timeSec", "halfLifeSec"],
    initial: ["remaining", "timeSec", "halfLifeSec"],
    time: ["initial", "remaining", "halfLifeSec"],
    halfLife: ["initial", "remaining", "timeSec"],
  }[solve] as (keyof Known)[];
  if (needed.some((key) => known[key] === null)) {
    return null;
  }
  const problem = checkKnown(known);
  if (problem) {
    return { error: problem };
  }
  const { initial, remaining, timeSec, halfLifeSec } = known;
  if (solve === "remaining") {
    const k = (timeSec as number) / (halfLifeSec as number);
    return finish({
      initial: initial as number,
      remaining: (initial as number) * fractionAfter(k),
      timeSec: timeSec as number,
      halfLifeSec: halfLifeSec as number,
    });
  }
  if (solve === "initial") {
    const k = (timeSec as number) / (halfLifeSec as number);
    return finish({
      initial: (remaining as number) / fractionAfter(k),
      remaining: remaining as number,
      timeSec: timeSec as number,
      halfLifeSec: halfLifeSec as number,
    });
  }
  const halfLives = Math.log2((initial as number) / (remaining as number));
  if (solve === "time") {
    return finish({
      initial: initial as number,
      remaining: remaining as number,
      timeSec: halfLives * (halfLifeSec as number),
      halfLifeSec: halfLifeSec as number,
    });
  }
  // Half-life from the other three: N must really have fallen over a positive time.
  if (halfLives === 0 || (timeSec as number) === 0) {
    return { error: "noDecay" };
  }
  return finish({
    initial: initial as number,
    remaining: remaining as number,
    timeSec: timeSec as number,
    halfLifeSec: (timeSec as number) / halfLives,
  });
}

export interface DecayRow {
  halfLives: number;
  /** ½^k */
  fraction: number;
  /** Remaining amount, N₀ · ½^k */
  amount: number;
  /** Elapsed time k · T½, seconds */
  timeSec: number;
}

export const TABLE_HALF_LIVES = 10;

/** The amount left after each whole number of half-lives, 0 to 10. */
export function decayTable(
  initial: number,
  halfLifeSec: number,
  rows = TABLE_HALF_LIVES
): DecayRow[] {
  return Array.from({ length: rows + 1 }, (_, k) => ({
    halfLives: k,
    fraction: fractionAfter(k),
    amount: initial * fractionAfter(k),
    timeSec: k * halfLifeSec,
  }));
}

/** ½^k written as a fraction: 1, 1/2, 1/4 … */
export function fractionText(halfLives: number): string {
  return halfLives === 0 ? "1" : `1/${2 ** halfLives}`;
}

export interface Preset {
  id: string;
  /** Nuclide notation, not translated. */
  label: string;
  /** Half-life as typed, rounded; sources differ in later digits. */
  value: string;
  unit: string;
}

/**
 * Common half-lives, rounded to three or four significant figures. Evaluated values
 * (ENSDF/NNDC and the DDEP evaluations) differ in the last digit or two between
 * editions; C-14 is the Cambridge value of 5,730 y used by radiocarbon laboratories
 * for the physical half-life.
 */
export const PRESETS: readonly Preset[] = [
  { id: "h3", label: "³H", value: "12.32", unit: "y" },
  { id: "c14", label: "¹⁴C", value: "5730", unit: "y" },
  { id: "f18", label: "¹⁸F", value: "109.8", unit: "min" },
  { id: "tc99m", label: "⁹⁹ᵐTc", value: "6.01", unit: "h" },
  { id: "i131", label: "¹³¹I", value: "8.02", unit: "d" },
  { id: "co60", label: "⁶⁰Co", value: "5.271", unit: "y" },
  { id: "sr90", label: "⁹⁰Sr", value: "28.8", unit: "y" },
  { id: "cs137", label: "¹³⁷Cs", value: "30.1", unit: "y" },
  { id: "rn222", label: "²²²Rn", value: "3.82", unit: "d" },
  { id: "po210", label: "²¹⁰Po", value: "138.4", unit: "d" },
  { id: "ra226", label: "²²⁶Ra", value: "1600", unit: "y" },
  { id: "pu239", label: "²³⁹Pu", value: "24110", unit: "y" },
  { id: "u238", label: "²³⁸U", value: "4.468", unit: "Gy" },
];
